package com.evoapps.evoshift.ui.user

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.filled.HealthAndSafety
import androidx.compose.material.icons.filled.School
import androidx.compose.material.icons.filled.Star
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.CheckStatus
import com.evoapps.evoshift.domain.model.Competence
import com.evoapps.evoshift.domain.model.DayStaffAlert
import com.evoapps.evoshift.domain.model.DayStaffWarning
import com.evoapps.evoshift.domain.model.TimeWindow
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.domain.model.StaffCheck
import com.evoapps.evoshift.domain.model.StaffReadiness
import com.evoapps.evoshift.domain.model.ShiftLeadRule
import com.evoapps.evoshift.domain.usecase.staff.StaffAlerts
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.widgets.RegularCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.dashboard_staff_checks
import evoshift.composeapp.generated.resources.dashboard_staff_expired
import evoshift.composeapp.generated.resources.dashboard_staff_expiring
import evoshift.composeapp.generated.resources.roster_day_no_lead
import evoshift.composeapp.generated.resources.roster_day_only_supervised
import evoshift.composeapp.generated.resources.roster_day_unsupervised_hours
import evoshift.composeapp.generated.resources.staff_check_medical_lower
import evoshift.composeapp.generated.resources.staff_check_ohs_lower
import evoshift.composeapp.generated.resources.staff_chip_medical_expired
import evoshift.composeapp.generated.resources.staff_chip_medical_expiring
import evoshift.composeapp.generated.resources.staff_chip_ohs_expired
import evoshift.composeapp.generated.resources.staff_chip_ohs_expiring
import evoshift.composeapp.generated.resources.staff_competence_independent
import evoshift.composeapp.generated.resources.staff_competence_supervised
import evoshift.composeapp.generated.resources.staff_date_and_status
import evoshift.composeapp.generated.resources.staff_shift_lead
import evoshift.composeapp.generated.resources.staff_status_expired
import evoshift.composeapp.generated.resources.staff_status_expiring
import evoshift.composeapp.generated.resources.staff_status_missing
import evoshift.composeapp.generated.resources.staff_status_valid
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import org.jetbrains.compose.resources.stringResource

/*
 * SH-47: the small pieces every screen shows of the staff fields: statuses and their colours,
 * the shift-lead star, the "needs supervision" mark, the team list's expiry chips, the dashboard
 * card and the roster day's warning.
 */

val ExpiredRed = Color(0xFFD32F2F)
val ExpiringAmber = Color(0xFFB26A00)
private val ValidGreen = Color(0xFF2E7D32)

@Composable
fun CheckStatus.label(): String = stringResource(
    when (this) {
        CheckStatus.VALID -> Res.string.staff_status_valid
        CheckStatus.EXPIRING -> Res.string.staff_status_expiring
        CheckStatus.EXPIRED -> Res.string.staff_status_expired
        CheckStatus.MISSING -> Res.string.staff_status_missing
    }
)

fun CheckStatus.color(onDark: Boolean = false): Color = when (this) {
    CheckStatus.VALID -> if (onDark) Color(0xFF81C784) else ValidGreen
    CheckStatus.EXPIRING -> if (onDark) Color(0xFFFFB74D) else ExpiringAmber
    CheckStatus.EXPIRED -> if (onDark) Color(0xFFEF9A9A) else ExpiredRed
    CheckStatus.MISSING -> if (onDark) Color.White.copy(alpha = 0.6f) else DeepNavy.copy(alpha = 0.6f)
}

@Composable
fun Competence.label(): String = stringResource(
    when (this) {
        Competence.INDEPENDENT -> Res.string.staff_competence_independent
        Competence.NEEDS_SUPERVISION -> Res.string.staff_competence_supervised
    }
)

/** "medical exam" / "OHS training", for sentences. */
@Composable
fun StaffCheck.lowerLabel(): String = stringResource(
    when (this) {
        StaffCheck.MEDICAL_EXAM -> Res.string.staff_check_medical_lower
        StaffCheck.OHS_TRAINING -> Res.string.staff_check_ohs_lower
    }
)

/** A day as people write it in Poland and the UK: 31.05.2027. */
fun formatDay(date: LocalDate): String =
    "${date.day.toString().padStart(2, '0')}.${date.month.number.toString().padStart(2, '0')}.${date.year}"

/** "31.05.2027 · Expires soon" (or the status alone without a date). */
@Composable
fun dateWithStatus(date: LocalDate?, status: CheckStatus?): String {
    val statusText = (status ?: CheckStatus.MISSING).label()
    return if (date == null) statusText else stringResource(Res.string.staff_date_and_status, formatDay(date), statusText)
}

/** A label and a value coloured by [status] (the profile sections' rows). */
@Composable
fun StatusDataRow(label: String, value: String, status: CheckStatus?) {
    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
        Text(label, style = MaterialTheme.typography.bodyMedium, color = DeepNavy.copy(alpha = 0.6f), fontWeight = FontWeight.Medium,
            modifier = Modifier.weight(1f))
        Text(value, style = MaterialTheme.typography.bodyMedium, color = (status ?: CheckStatus.MISSING).color(), fontWeight = FontWeight.Bold,
            modifier = Modifier.padding(start = 16.dp), textAlign = TextAlign.End)
    }
}

/**
 * The shift-lead star and the "needs supervision" mark of [userId] with [engagement] (nothing for
 * an independent employee who does not lead), with their meaning as content descriptions. Who
 * leads is the workplace's [leadRule] (SH-104).
 */
@Composable
fun StaffBadges(
    engagement: Engagement?,
    leadRule: ShiftLeadRule,
    userId: String?,
    tint: Color = DarkGold,
    size: Dp = 14.dp,
    modifier: Modifier = Modifier
) {
    if (engagement == null) return
    val leads = leadRule.leads(engagement, userId)
    val supervised = leadRule.needsSupervision(engagement, userId)
    if (!leads && !supervised) return
    Row(modifier, verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(2.dp)) {
        if (leads) {
            Icon(Icons.Default.Star, contentDescription = stringResource(Res.string.staff_shift_lead), tint = tint, modifier = Modifier.size(size))
        }
        if (supervised) {
            Icon(Icons.Default.School, contentDescription = stringResource(Res.string.staff_competence_supervised),
                tint = ExpiringAmber, modifier = Modifier.size(size))
        }
    }
}

/** The team list's chips: each check that has expired or ends within 30 days. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun StaffCheckChips(readiness: StaffReadiness?, modifier: Modifier = Modifier) {
    if (readiness == null || !readiness.needsAttention) return
    FlowRow(modifier, horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
        listOf(
            readiness.medical to (Res.string.staff_chip_medical_expired to Res.string.staff_chip_medical_expiring),
            readiness.ohs to (Res.string.staff_chip_ohs_expired to Res.string.staff_chip_ohs_expiring)
        ).forEach { (status, texts) ->
            val text = when (status) {
                CheckStatus.EXPIRED -> texts.first
                CheckStatus.EXPIRING -> texts.second
                else -> null
            } ?: return@forEach
            Text(
                stringResource(text),
                style = MaterialTheme.typography.labelSmall,
                color = Color.White,
                fontWeight = FontWeight.Bold,
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .background(status.color())
                    .padding(horizontal = 8.dp, vertical = 2.dp)
            )
        }
    }
}

/** The dashboards' to-do: people whose medical exam or OHS training expired or ends soon (SH-47). */
@Composable
fun StaffChecksCard(alerts: StaffAlerts, onClick: () -> Unit, modifier: Modifier = Modifier) {
    RegularCard(modifier = modifier.fillMaxWidth(), onClick = onClick) {
        Row(
            modifier = Modifier.padding(16.dp).fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(Icons.Default.HealthAndSafety, null, tint = if (alerts.expired > 0) ExpiredRed else ExpiringAmber)
            Spacer(Modifier.width(16.dp))
            Column(Modifier.weight(1f)) {
                Text(
                    stringResource(Res.string.dashboard_staff_checks),
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                    color = DeepNavy.copy(alpha = 0.7f)
                )
                Spacer(Modifier.height(4.dp))
                if (alerts.expired > 0) {
                    Text(stringResource(Res.string.dashboard_staff_expired, alerts.expired), style = MaterialTheme.typography.bodyMedium,
                        color = ExpiredRed, fontWeight = FontWeight.Bold)
                }
                if (alerts.expiring > 0) {
                    Text(stringResource(Res.string.dashboard_staff_expiring, alerts.expiring), style = MaterialTheme.typography.bodyMedium,
                        color = ExpiringAmber, fontWeight = FontWeight.Bold)
                }
            }
            Icon(Icons.AutoMirrored.Filled.ArrowForward, null, tint = DeepNavy.copy(alpha = 0.7f))
        }
    }
}

/** What a roster day is missing, in words (with the hours when only people who need supervision work). */
@Composable
fun DayStaffAlert.text(): String = when (warning) {
    DayStaffWarning.NO_SHIFT_LEAD -> stringResource(Res.string.roster_day_no_lead)
    DayStaffWarning.ONLY_NEEDS_SUPERVISION ->
        if (windows.isEmpty()) stringResource(Res.string.roster_day_only_supervised)
        else stringResource(Res.string.roster_day_unsupervised_hours, windows.joinToString(", ") { it.clockLabel() })
}

/** "6:00–10:00" (local clock times; a window into the next day ends at its clock time). */
fun TimeWindow.clockLabel(): String {
    fun clock(minutes: Int) = "${minutes / 60}:${(minutes % 60).toString().padStart(2, '0')}"
    val end = if (endMinuteOfDay == 0) "24:00" else clock(endMinuteOfDay)
    return "${clock(startMinuteOfDay)}–$end"
}
